import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, ArrowSquareOut } from "@phosphor-icons/react";

const EMPTY = { title: "", intro: "", category: "", product_ids: [], labels: {} };
const input = "w-full px-3 py-2.5 rounded-lg border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#5B7B53]/30";

function ComparisonForm({ form, setForm, products }) {
  const byId = Object.fromEntries(products.map((p) => [p.id, p]));
  const add = (id) => id && !form.product_ids.includes(id) && setForm({ ...form, product_ids: [...form.product_ids, id] });
  const remove = (id) => setForm({ ...form, product_ids: form.product_ids.filter((x) => x !== id) });
  const move = (i, d) => {
    const ids = [...form.product_ids];
    [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    setForm({ ...form, product_ids: ids });
  };
  return (
    <div className="space-y-4">
      <input data-testid="cmp-form-title" placeholder="Title, e.g. Air Fryers Compared" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={input} />
      <input data-testid="cmp-form-category" placeholder="Category (optional)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={input} />
      <textarea data-testid="cmp-form-intro" rows={3} placeholder="Short intro shown above the table" value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} className={input} />
      <select data-testid="cmp-form-add-product" value="" onChange={(e) => add(e.target.value)} className={input}>
        <option value="">+ Add a product to compare…</option>
        {products.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
      </select>
      <ol className="space-y-2">
        {form.product_ids.map((id, i) => (
          <li key={id} className="flex flex-wrap items-center gap-2 border border-[#E5E0D8] rounded-lg p-2 text-sm">
            <span className="w-5 text-[#5C5A56]">{i + 1}.</span>
            <span className="flex-1 min-w-[10rem] truncate">{byId[id]?.title || id}</span>
            <input data-testid={`cmp-form-label-${i}`} placeholder="Best for… (optional)" value={form.labels[id] || ""} onChange={(e) => setForm({ ...form, labels: { ...form.labels, [id]: e.target.value } })} className="px-2 py-1.5 rounded border border-[#E5E0D8] text-xs w-40" />
            <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="px-2 disabled:opacity-30">↑</button>
            <button type="button" disabled={i === form.product_ids.length - 1} onClick={() => move(i, 1)} className="px-2 disabled:opacity-30">↓</button>
            <button type="button" data-testid={`cmp-form-remove-${i}`} onClick={() => remove(id)} className="px-2 text-[#B5613F]">✕</button>
          </li>
        ))}
      </ol>
      <p className="text-xs text-[#5C5A56]">The first product is highlighted as "Best overall". Empty labels are filled automatically (Best value, Highest rated, Most popular).</p>
    </div>
  );
}

export function AdminComparisons() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const load = () => api.get("/comparisons").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/products", { params: { limit: 500 } }).then((r) => setProducts(r.data)); }, []);

  const open = (c) => { setEditing(c || {}); setForm(c ? { ...EMPTY, ...c } : EMPTY); };
  const save = async () => {
    if (!form.title.trim() || form.product_ids.length < 2) return toast.error("Add a title and at least 2 products");
    const body = { ...form, labels: Object.fromEntries(Object.entries(form.labels).filter(([k, v]) => v && form.product_ids.includes(k))) };
    try {
      if (editing.id) await api.put(`/comparisons/${editing.id}`, body); else await api.post("/comparisons", body);
      toast.success("Comparison saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete "${c.title}"?`)) return;
    await api.delete(`/comparisons/${c.id}`);
    load();
  };

  return (
    <div data-testid="admin-comparisons">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-4xl text-[#2C2B29]">Comparisons</h1>
        <button data-testid="cmp-new-btn" onClick={() => open(null)} className="hh-btn-primary rounded-full px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2"><Plus size={14} /> New comparison</button>
      </div>
      <div className="bg-white border border-[#E5E0D8] rounded-2xl divide-y divide-[#E5E0D8]">
        {items.map((c) => (
          <div key={c.id} data-testid={`cmp-row-${c.slug}`} className="flex items-center gap-4 p-4">
            <div className="flex -space-x-2">{c.thumbs.map((t) => <img key={t} src={t} alt="" className="w-10 h-10 rounded-full object-cover border-2 border-white" />)}</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[#2C2B29] truncate">{c.title}</p>
              <p className="text-xs text-[#5C5A56]">{c.product_ids.length} products · /compare/{c.slug}</p>
            </div>
            <a href={`/compare/${c.slug}`} target="_blank" rel="noreferrer" className="p-2 text-[#5C5A56]" aria-label="View"><ArrowSquareOut size={16} /></a>
            <button data-testid={`cmp-edit-${c.slug}`} onClick={() => open(c)} className="p-2 text-[#5C5A56]" aria-label="Edit"><PencilSimple size={16} /></button>
            <button data-testid={`cmp-delete-${c.slug}`} onClick={() => del(c)} className="p-2 text-[#B5613F]" aria-label="Delete"><Trash size={16} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="p-6 text-sm text-[#5C5A56]">No comparisons yet.</p>}
      </div>
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="cmp-modal">
            <h2 className="font-display text-2xl mb-5">{editing.id ? "Edit comparison" : "New comparison"}</h2>
            <ComparisonForm form={form} setForm={setForm} products={products} />
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full text-sm border border-[#E5E0D8]">Cancel</button>
              <button data-testid="cmp-save-btn" onClick={save} className="hh-btn-primary px-5 py-2.5 rounded-full text-sm font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
